import { format, parse } from "date-fns";
import type React from "react";
import { useEffect, useState } from "react";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { CommunityGroupV2Dto } from "@/api/types";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { GroupIcon } from "@/components/GroupIcon/GroupIcon";
import { Button } from "@/design-system/Button";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

export type CommunityFeedFilterValues = {
  startDate?: string;
  endDate?: string;
  groupIds?: string[];
};

type CommunityFeedFilterDrawerProps = DrawerBaseProps & {
  filter: CommunityFeedFilterValues;
  filterOptions: {
    groups: CommunityGroupV2Dto[];
    helpCategories: CommunityGroupV2Dto[];
  };
  onUpdate: (filter: CommunityFeedFilterValues) => void;
};

export function CommunityFeedFilterDrawer({
  filter,
  onUpdate,
  filterOptions,
  ...drawerProps
}: CommunityFeedFilterDrawerProps): React.ReactNode {
  const [currFilter, setCurrFilter] = useState<CommunityFeedFilterValues>({
    groupIds: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  const { t } = useTranslation();
  const resolveImage = useImageResolver();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setCurrFilter(filter);
  }, [filter]);

  const handleApplyFilter = () => {
    onUpdate(currFilter);
  };

  const handleClearFilter = () => {
    onUpdate({
      startDate: undefined,
      endDate: undefined,
      groupIds: undefined,
    });
  };

  return (
    <Drawer.Root title={t("page.message-feed.filter-drawer.title")} {...drawerProps}>
      <Drawer.Body>
        <Stack gap="md">
          <FormField label={t("page.message-feed.filter-drawer.start-date.label")}>
            <DatePicker
              placeholder={t("page.message-feed.filter-drawer.start-date.placeholder")}
              value={currFilter.startDate ? parse(currFilter.startDate, "yyyy-MM-dd", new Date(0)) : undefined}
              onChange={(value) =>
                setCurrFilter((prev) => ({ ...prev, startDate: value ? format(value, "yyyy-MM-dd") : undefined }))
              }
              max={currFilter.endDate ? parse(currFilter.endDate, "yyyy-MM-dd", new Date(0)) : undefined}
            />
          </FormField>

          <FormField label={t("page.message-feed.filter-drawer.end-date.label")}>
            <DatePicker
              placeholder={t("page.message-feed.filter-drawer.end-date.placeholder")}
              value={currFilter.endDate ? parse(currFilter.endDate, "yyyy-MM-dd", new Date(0)) : undefined}
              onChange={(value) =>
                setCurrFilter((prev) => ({ ...prev, endDate: value ? format(value, "yyyy-MM-dd") : undefined }))
              }
              min={currFilter.startDate ? parse(currFilter.startDate, "yyyy-MM-dd", new Date(0)) : undefined}
            />
          </FormField>
          <FormField label={t("page.message-feed.filter-drawer.group.label")}>
            <CheckboxMultiSelect<CommunityGroupV2Dto>
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              placeholder={t("page.message-feed.filter-drawer.group.placeholder")}
              selected={
                currFilter.groupIds ? filterOptions.groups.filter((x) => currFilter.groupIds!.includes(x.id)) : []
              }
              items={filterOptions.groups}
              onChange={(newGroups) =>
                setCurrFilter((prev) => ({
                  ...prev,
                  groupIds: prev.groupIds
                    ? [
                        ...filterOptions.helpCategories.filter((g) => prev.groupIds!.includes(g.id)).map((g) => g.id),
                        ...newGroups.map((x) => x.id),
                      ]
                    : newGroups.map((x) => x.id),
                }))
              }
              keySelector={(x) => x.id}
              renderOption={(x) => (
                <Inline gap="sm" align="center">
                  {x.isResidentGroup && x.image ? (
                    <img src={resolveImage(x.image, "sm")} alt={x.name} className="size-icon-prominent rounded-full" />
                  ) : (
                    <span className="size-icon-prominent p-space-xs">
                      <GroupIcon icon={x.icon} />
                    </span>
                  )}
                  <span>{x.name}</span>
                </Inline>
              )}
            />
          </FormField>

          <FormField label={t("page.message-feed.filter-drawer.help-category.label")}>
            <CheckboxMultiSelect<CommunityGroupV2Dto>
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              placeholder={t("page.message-feed.filter-drawer.help-category.placeholder")}
              selected={
                currFilter.groupIds
                  ? filterOptions.helpCategories.filter((x) => currFilter.groupIds!.includes(x.id))
                  : []
              }
              items={filterOptions.helpCategories}
              onChange={(newGroups) =>
                setCurrFilter((prev) => ({
                  ...prev,
                  groupIds: prev.groupIds
                    ? [
                        ...filterOptions.groups.filter((g) => prev.groupIds!.includes(g.id)).map((g) => g.id),
                        ...newGroups.map((x) => x.id),
                      ]
                    : newGroups.map((x) => x.id),
                }))
              }
              keySelector={(x) => x.id}
              renderOption={(x) => (
                <Inline gap="sm" align="center">
                  {x.isResidentGroup && x.image ? (
                    <img src={resolveImage(x.image, "sm")} alt={x.name} className="size-icon-prominent rounded-full" />
                  ) : (
                    <span className="size-icon-prominent p-space-xs">
                      <GroupIcon icon={x.icon} />
                    </span>
                  )}
                  <span>{x.name}</span>
                </Inline>
              )}
            />
          </FormField>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClearFilter}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button onClick={handleApplyFilter}>{t("common.action.apply")}</Button>
        </Drawer.Close>
      </Drawer.Actions>
    </Drawer.Root>
  );
}
